import React from "react";

import styled from "styled-components/macro";

import ManagePaymentMethods from "Payments/ManagePaymentMethods";
import { sidePadding } from "Payments/PaymentPage/PaymentFormScreen/styles";
import { usePaymentPage } from "Payments/PaymentPage/PaymentPageContext";
import {
  useBankProvider,
  useCardProvider,
  useMerchantToken,
} from "Payments/globalPaymentsStateHooks";
import { motion } from "framer-motion";

import FnrIcon from "UI/FnrIcon";

import { ScreenWrapper } from "../Shared/ScreenWrapper";

export default function ManagePaymentMethodsScreen({ showing, setFormStage }) {
  const {
    state: {
      network,
      user,
      paymentMethods,
      fund: { fundColorSafe },
      tabID,
    },
    dispatch,
  } = usePaymentPage();

  const { provider: cardProvider } = useCardProvider();
  const { provider: bankProvider } = useBankProvider();
  const merchantToken = useMerchantToken();
  const paymentProvider = React.useMemo(() => {
    return { card: cardProvider, bank: bankProvider, merchantToken };
  }, [bankProvider, cardProvider, merchantToken]);

  const initialUserData = {
    paymentMethodsCanBeShared: user?.paymentMethodsCanBeShared,
    otherHoHNames: user?.sharableWith,
    hasMobilePhone: user?.phoneNumber && true,
    userID: user?.ID,
    name: user?.name,
    zip: user?.billingAddress?.zip,
  };

  const handleBackClick = () => {
    dispatch({
      type: "setScreenControlFlag",
      flag: "isManagingPaymentMethods",
      value: false,
    });
  };

  const updatePaymentMethods = (paymentMethods) => {
    dispatch({
      type: "setPaymentMethods",
      payload: paymentMethods,
    });
  };

  const onCardSelect = (paymentMethodData) => {
    dispatch({
      type: "setSelectedPaymentMethodIDAndPaymentMethods",
      payload: {
        paymentMethods: paymentMethods,
        selectedPaymentMethodID: paymentMethodData.ID,
      },
    });
    dispatch({
      type: "setScreenControlFlag",
      flag: "isManagingPaymentMethods",
      value: false,
    });
    dispatch({
      type: "updateAddOnGiftsAfterPaymentMethodSelect",
      payload: paymentMethodData,
    });
    setFormStage("methodSelect");
  };

  const getTitleNoun = () => {
    const hasCards = paymentMethods?.some((pm) => pm.paymentType === "card");
    const hasBanks = paymentMethods?.some((pm) => pm.paymentType === "bank");

    if (hasCards && hasBanks) return "payment method";
    else if (!hasCards && !hasBanks) return "payment method";
    else if (!hasBanks) return "card";
    else return "account";
  };

  return (
    <Wrapper>
      <Inside>
        <Header>
          <BackButton
            whileHover={{ scale: 1.05, color: fundColorSafe }}
            whileTap={{ scale: 0.98, color: fundColorSafe }}
            onClick={handleBackClick}
          >
            <FnrIcon fas fa-chevron-circle-left />
          </BackButton>
          <Title>{`Select the ${getTitleNoun()} you'd like to use`}</Title>
        </Header>
        <ManagePaymentMethods
          onBackClick={handleBackClick}
          onCardSelect={onCardSelect}
          initialPaymentMethods={paymentMethods}
          initialUserData={initialUserData}
          updateExternalPaymentMethods={updatePaymentMethods}
          showQuickControls={false}
          show={showing}
          tabID={tabID}
          networkName={network?.name}
          forceIsCardAllowed={network.isCardAllowed}
          forceIsACHAllowed={network.isACHAllowed}
        />
      </Inside>
    </Wrapper>
  );
}

const Wrapper = styled(ScreenWrapper)`
  padding: 24px 0;
`;
const Inside = styled.div`
  ${sidePadding}
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 24px;
  position: relative;
  z-index: 1000;
`;

const Title = styled.div`
  font-size: 24px;
  font-weight: 400;
  line-height: 32px;
  max-width: 300px;
  color: var(--fundColor);
`;

const BackButton = styled(motion.div)`
  font-size: 32px;
  cursor: pointer;
  color: #828c8d;
  flex-shrink: 0;
  flex-grow: 0;
`;
